/**
 * Text layout calculator for handwriting generation.
 * Handles CJK text measurement, line breaking, and page splitting.
 */

/**
 * Default layout options (matching original Python backend defaults).
 */
export const DEFAULT_LAYOUT = {
  width: 2481,
  height: 3507,
  fontSize: 124,
  lineSpacing: 200,
  marginTop: 50,
  marginBottom: 50,
  marginLeft: 50,
  marginRight: 50,
  wordSpacing: 1,
};

/**
 * Calculate text layout for handwriting rendering.
 *
 * @param {string} text - The text to lay out
 * @param {object} options - Layout options
 * @param {number} options.width - Page width in pixels
 * @param {number} options.height - Page height in pixels
 * @param {number} options.fontSize - Font size in pixels
 * @param {number} options.lineSpacing - Line spacing in pixels
 * @param {number} options.marginTop - Top margin in pixels
 * @param {number} options.marginBottom - Bottom margin in pixels
 * @param {number} options.marginLeft - Left margin in pixels
 * @param {number} options.marginRight - Right margin in pixels
 * @param {number} options.wordSpacing - Extra space between characters
 * @param {CanvasRenderingContext2D} ctx - Canvas context for text measurement
 * @returns {Array<{pageIndex: number, lines: Array<{text: string, chars: Array<{char: string, x: number, y: number, width: number}>}>}>}
 */
export function calculateLayout(text, options, ctx) {
  const {
    width,
    height,
    fontSize,
    lineSpacing,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    wordSpacing,
  } = { ...DEFAULT_LAYOUT, ...options };

  const contentWidth = width - marginLeft - marginRight;
  const usableHeight = height - marginTop - marginBottom;
  const linesPerPage = Math.floor(usableHeight / lineSpacing);
  const fontStr = `${fontSize}px sans-serif`;

  ctx.font = fontStr;

  const pages = [];
  let currentPage = { pageIndex: 0, lines: [] };
  let currentLine = { text: '', chars: [] };
  let lineX = marginLeft;
  let charIndex = 0;

  // Chinese punctuation that should not start a line
  const endChars = new Set([
    '，', '。', '！', '？', '、', '：', '；', '」', '》', '）',
    '》', '』', '】', '〉', '』', '」', '』', '』',
    ',', '.', '!', '?', ':', ';', ')', ']', '}',
  ]);

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    // Handle newlines
    if (char === '\n') {
      currentPage.lines.push(currentLine);
      currentLine = { text: '', chars: [] };
      lineX = marginLeft;
      charIndex++;

      if (currentPage.lines.length >= linesPerPage) {
        pages.push(currentPage);
        currentPage = { pageIndex: pages.length, lines: [] };
      }
      continue;
    }

    // Handle carriage return
    if (char === '\r') {
      continue;
    }

    // Measure character width
    const charMetrics = ctx.measureText(char);
    const charWidth = charMetrics.width + wordSpacing;

    // Check if this character would overflow the line
    if (lineX + charWidth > marginLeft + contentWidth && currentLine.chars.length > 0) {
      // If overflow char is an end-char, pull the last char from the line
      if (endChars.has(char) && currentLine.chars.length > 0) {
        const lastCharData = currentLine.chars.pop();
        currentLine.text = currentLine.text.slice(0, -1);
        lineX -= lastCharData.width;
      }

      currentPage.lines.push(currentLine);
      currentLine = { text: '', chars: [] };
      lineX = marginLeft;

      if (currentPage.lines.length >= linesPerPage) {
        pages.push(currentPage);
        currentPage = { pageIndex: pages.length, lines: [] };
      }
    }

    // Add character to current line
    currentLine.text += char;
    currentLine.chars.push({
      char,
      x: lineX,
      y: marginTop + (currentPage.lines.length) * lineSpacing + fontSize,
      width: charWidth,
      index: charIndex,
    });

    lineX += charWidth;
    charIndex++;
  }

  // Push the last line
  if (currentLine.chars.length > 0) {
    currentPage.lines.push(currentLine);
  }

  // Push the last page if it has content
  if (currentPage.lines.length > 0) {
    pages.push(currentPage);
  }

  // If no pages were created, create one empty page
  if (pages.length === 0) {
    pages.push({ pageIndex: 0, lines: [] });
  }

  return pages;
}

/**
 * Estimate the number of pages for a given text.
 *
 * @param {string} text - The text to estimate
 * @param {object} options - Layout options
 * @returns {number} Estimated page count
 */
export function estimatePages(text, options) {
  const {
    width = 2481,
    height = 3507,
    fontSize = 124,
    lineSpacing = 200,
    marginTop = 50,
    marginBottom = 50,
    marginLeft = 50,
    marginRight = 50,
  } = { ...DEFAULT_LAYOUT, ...options };

  const usableWidth = width - marginLeft - marginRight;
  const usableHeight = height - marginTop - marginBottom;
  const linesPerPage = Math.floor(usableHeight / lineSpacing);
  const avgCharWidth = fontSize * 0.8;
  const charsPerLine = Math.floor(usableWidth / avgCharWidth);
  const charsPerPage = charsPerLine * linesPerPage;

  if (!text || text.length === 0) return 0;
  return Math.ceil(text.length / charsPerPage);
}

export default calculateLayout;
